import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import DataSetsItemButtons from 'components/DataSetsItemButtons/DataSetsItemButtons';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';
import TableModal from 'components/TableModal/TableModal';
import { MILLION, TABLE_HEADERS_CUSTOMERS } from 'constants/dataSets';
import { SELECTED_ITEMS } from 'constants/formFields';
import { NUMBER_FORMATS } from 'constants/interfaces';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import formatNumber from 'utils/formatNumber';
import formattedData from 'utils/formattedData';
import formattedNumberWithComma from 'utils/formattedNumberWithComma';
import getNaceLabel from 'utils/getNaceLabel';

import type { ICustomersItem } from './interface/ICustomersItem';

import styles from './scss/CustomersItem.module.scss';

function CustomersItem({
  item, control, onArchiveModalInfoOpen, onMoveToArchive, onDeArchive, onEditRecord, onDeleteRecord, isActionsVisible,
  naceCodeTreeData, locationsCountries,
}: ICustomersItem): React.ReactElement {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;
  const {
    id, name, code, profitCenterLinks, addressBuilding, addressCity, addressCountry, addressPostCode, addressState,
    addressStreet, businessUnitType, dateClosed, priorityRating, dateOpened, naceCode, equityShareholding,
    financialControl, euTaxonomyCode, ownership, uniqueIdentifier, valueChainSection, latestArchivation,
    naceSection, addressCoordinates, archived,
  } = item;

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  const { naceCodeLabel, naceSectionLabel } = getNaceLabel(
    { naceCode, naceSection, naceCodeTreeData },
  );

  const hyphen = t('common.hyphen');

  const handleToggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  const formattedProfitCenterLinks = profitCenterLinks?.map((link) => ({
    ...link,
    startDate: link.startDate ? new Date(link.startDate).toLocaleDateString() : '',
    endDate: link.endDate ? new Date(link.endDate).toLocaleDateString() : '',
    totalCost: link.totalCost
      ? `${formattedNumberWithComma((Number(link.totalCost.split(' ')[0]) / MILLION).toFixed(2))} M ${companyCurrency}`
      : hyphen,
  }));

  const tableCellData = [
    { id: uniqueIdentifier, content: uniqueIdentifier || hyphen },
    { id: code, content: code || hyphen },
    { id: valueChainSection, content: valueChainSection || hyphen },
    { id: ownership, content: ownership || hyphen },
    { id: businessUnitType, content: businessUnitType || hyphen },
    {
      id: equityShareholding,
      content: formatNumber({ value: equityShareholding, type: NUMBER_FORMATS.percent }),
    },
    {
      id: financialControl,
      content: formatNumber({ value: financialControl, type: NUMBER_FORMATS.percent }),
    },
    { id: dateOpened, content: formattedData(dateOpened) || hyphen },
    { id: dateClosed, content: formattedData(dateClosed) || hyphen },
    { id: priorityRating, content: priorityRating || hyphen },
    { id: naceSectionLabel, content: naceSectionLabel || hyphen },
    { id: naceCodeLabel, content: naceCodeLabel || hyphen },
    { id: euTaxonomyCode, content: euTaxonomyCode || hyphen },
  ];

  return (
    <>
      <TableRow className={clsx(undefined, { [styles.archived]: archived })}>
        <TableCell>
          <div className={styles.staticCell}>
            { archived ? (
              <span>
                {name}
                {latestArchivation ? (
                  <InfoIcon
                    onClick={() => onArchiveModalInfoOpen(latestArchivation)}
                  />
                ) : null}
              </span>
            ) : null}

            {(!archived) && (
              isActionsVisible ? (
                <Controller
                  control={control}
                  render={({ field: { onChange, value, disabled } }) => (
                    <Checkbox
                      key={id}
                      name={`${SELECTED_ITEMS}-${id}`}
                      checked={!!value.includes(id)}
                      value={id}
                      disabled={disabled}
                      onChange={(event) => {
                        onChange(event.target.checked
                          ? [...value, id]
                          : value.filter((selectedItem) => selectedItem !== id));
                      }}
                      label={(<span>{name}</span>)}
                    />
                  )}
                  name={SELECTED_ITEMS}
                />
              ) : (<span>{name}</span>)
            )}
          </div>
        </TableCell>

        {tableCellData.map((cell, index) => (
          <TableCell key={[cell.id, index].join('_')}>
            {cell.content}
          </TableCell>
        ))}

        <TableCell>
          <div className={styles.profitCenterLinksContainer}>
            <div className={styles.wrapper}>
              {profitCenterLinks.slice(0, 3).map((profitCenterLink) => (
                <div key={profitCenterLink.profitCenterId}>
                  {profitCenterLink.profitCenterId}
                </div>
              ))}
              {profitCenterLinks.length > 3 && (
              <div>...</div>
              )}
            </div>
            <InfoIcon
              onClick={handleToggleModal}
            />
          </div>
        </TableCell>

        {[
          { id: 'addressCountry', content: locationCountry(addressCountry) || hyphen },
          { id: 'addressState', content: addressState || hyphen },
          { id: 'addressCity', content: addressCity || hyphen },
          { id: 'addressPostCode', content: addressPostCode || hyphen },
          { id: 'addressStreet', content: addressStreet || hyphen },
          { id: 'addressBuilding', content: addressBuilding || hyphen },
          { id: 'addressCoordinates', content: addressCoordinates || hyphen },
        ].map((cell) => (
          <TableCell key={cell.id}>
            {cell.content}
          </TableCell>
        ))}

        {isActionsVisible ? (
          <TableCell>
            <DataSetsItemButtons
              id={id}
              archived={archived}
              onEditRecord={onEditRecord}
              onMoveToArchive={onMoveToArchive}
              onDeArchive={onDeArchive}
              onDeleteRecord={onDeleteRecord}
            />
          </TableCell>
        ) : null}
      </TableRow>
      <TableModal
        open={isModalVisible}
        title={t('common.financials', { name })}
        data={formattedProfitCenterLinks}
        onClose={handleToggleModal}
        headers={TABLE_HEADERS_CUSTOMERS}
      />
    </>
  );
}

export default CustomersItem;
